import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ExternalLink, Layers, Loader2, Plus, Trash2 } from 'lucide-react';

const DESIGNS = [
  { key: 'klar', name: 'Klar' }, { key: 'balken', name: 'Balken' }, { key: 'seitenleiste', name: 'Seitenleiste' },
  { key: 'klassisch', name: 'Klassisch' }, { key: 'kompakt', name: 'Kompakt' }, { key: 'lex', name: 'lex Honorarnote' },
];
const SCENARIOS = [
  { key: 'standard', name: 'Standard (USt)' }, { key: 'small_business', name: 'Kleinunternehmer' },
  { key: 'reverse_charge', name: 'Reverse Charge (EU)' }, { key: 'ch_qr', name: 'CH mit QR-Rechnung' },
];
const PRESETS = ['#1e40af', '#0f766e', '#15803d', '#374151', '#7c2d12', '#c2410c'];
const DEFAULTS = {
  country: 'DE', scenario: 'standard', design: 'klar', accent_color: '#1e40af', show_badge: true, compact: false,
  seller: { name: 'Muster Consulting GmbH', street: 'Musterstraße', street_number: '1', postal_code: '10115', city: 'Berlin', country_code: 'DE', vat_id: 'DE123456789', email: 'office@muster.de' },
  buyer: { name: 'Kunde AG', street: 'Kundenweg', street_number: '12', postal_code: '80331', city: 'München', country_code: 'DE', vat_id: '' },
  items: [
    { description: 'Beratung & Konzeption', quantity: 12, unit: 'Std', unit_price: 120 },
    { description: 'Umsetzung Projektphase 1', quantity: 1, unit: 'Pausch.', unit_price: 1850 },
  ],
  fill_items: 0, closing_text: 'Vielen Dank für Ihren Auftrag.', logo_base64: null,
};
const COUNTRY_DEFAULTS = {
  DE: { postal_code: '10115', city: 'Berlin', vat: 'DE123456789' },
  AT: { postal_code: '1010', city: 'Wien', vat: 'ATU12345678' },
  CH: { postal_code: '8001', city: 'Zürich', vat: 'CHE-123.456.789 MWST' },
};

function PartyFields({ label, value, onChange, testPrefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <Input value={value.name} onChange={(e) => set('name', e.target.value)} placeholder="Name" data-testid={`${testPrefix}-name`} />
      <div className="grid grid-cols-[1fr_70px] gap-2">
        <Input value={value.street} onChange={(e) => set('street', e.target.value)} placeholder="Straße" />
        <Input value={value.street_number} onChange={(e) => set('street_number', e.target.value)} placeholder="Nr." />
      </div>
      <div className="grid grid-cols-[90px_1fr] gap-2">
        <Input value={value.postal_code} onChange={(e) => set('postal_code', e.target.value)} placeholder="PLZ" />
        <Input value={value.city} onChange={(e) => set('city', e.target.value)} placeholder="Ort" />
      </div>
      <Input value={value.vat_id || ''} onChange={(e) => set('vat_id', e.target.value)} placeholder="UID / USt-IdNr. (optional)" />
    </div>
  );
}

export default function InvoiceDesignerPanel() {
  const [form, setForm] = useState(DEFAULTS);
  const [preview, setPreview] = useState(null);
  const [pages, setPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const render = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await api.post('/admin/invoice-designer/preview', { ...f, format: 'png', all_designs: false }, { responseType: 'blob' });
      setPages(res.headers['x-page-count'] || null);
      const url = URL.createObjectURL(res.data);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen – bitte Eingaben prüfen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => render(form), 600);
    return () => clearTimeout(timer.current);
  }, [form, render]);

  const openPdf = async (all) => {
    try {
      const res = await api.post('/admin/invoice-designer/preview', { ...form, format: 'pdf', all_designs: all }, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank', 'noopener');
    } catch (e) {
      toast.error('PDF konnte nicht erzeugt werden');
    }
  };

  const setCountry = (cc) => {
    const d = COUNTRY_DEFAULTS[cc];
    set({ country: cc, seller: { ...form.seller, country_code: cc, postal_code: d.postal_code, city: d.city, vat_id: d.vat },
          buyer: { ...form.buyer, country_code: cc, postal_code: d.postal_code, city: d.city },
          scenario: cc !== 'CH' && form.scenario === 'ch_qr' ? 'standard' : form.scenario });
  };

  const onLogo = (file) => {
    if (!file) return set({ logo_base64: null });
    const r = new FileReader();
    r.onload = () => set({ logo_base64: String(r.result).split(',')[1] });
    r.readAsDataURL(file);
  };

  const updateItem = (i, k, v) => set({ items: form.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) });

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_420px]" data-testid="admin-invoice-designer">
      <div className="space-y-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Layers className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold">Rechnungs-Designer</h3>
          <span className="text-xs text-slate-500">Rendert mit dem echten Renderer – jede Eingabe aktualisiert die Vorschau.</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">Land</Label>
            <div className="flex gap-1">
              {['DE', 'AT', 'CH'].map((cc) => (
                <button key={cc} type="button" onClick={() => setCountry(cc)} data-testid={`designer-country-${cc}`}
                  className={`flex-1 rounded-md border px-2 py-1.5 text-xs ${form.country === cc ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200'}`}>{cc}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Szenario</Label>
            <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" value={form.scenario} onChange={(e) => set({ scenario: e.target.value })} data-testid="designer-scenario">
              {SCENARIOS.filter((s) => s.key !== 'ch_qr' || form.country === 'CH').map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Design</Label>
            <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" value={form.design} onChange={(e) => set({ design: e.target.value })} data-testid="designer-design">
              {DESIGNS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-xs">Akzentfarbe</Label>
          {PRESETS.map((c) => (
            <button key={c} type="button" aria-label={c} onClick={() => set({ accent_color: c })} data-testid={`designer-accent-${c.slice(1)}`}
              className={`h-7 w-7 rounded-full border-2 ${form.accent_color === c ? 'border-slate-900' : 'border-white shadow'}`} style={{ backgroundColor: c }} />
          ))}
          <Input value={form.accent_color} onChange={(e) => set({ accent_color: e.target.value })} className="h-8 w-28 font-mono text-xs" data-testid="designer-accent-hex" />
          <label className="ml-auto flex items-center gap-2 text-xs"><input type="checkbox" checked={form.show_badge} onChange={(e) => set({ show_badge: e.target.checked })} data-testid="designer-badge" /> Schriftzug „erstellt mit ivoliq.io“</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.compact} onChange={(e) => set({ compact: e.target.checked })} data-testid="designer-compact" /> Kompakt-Modus</label>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <PartyFields label="Aussteller" value={form.seller} onChange={(v) => set({ seller: v })} testPrefix="designer-seller" />
          <PartyFields label="Empfänger" value={form.buyer} onChange={(v) => set({ buyer: v })} testPrefix="designer-buyer" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Positionen</Label>
            <div className="flex items-center gap-2 text-xs">
              <span>Zusätzlich generieren:</span>
              <Input type="number" min={0} max={80} value={form.fill_items} onChange={(e) => set({ fill_items: Math.max(0, Math.min(80, Number(e.target.value) || 0)) })} className="h-7 w-16 text-xs" data-testid="designer-fill-items" />
              <Button type="button" size="sm" variant="outline" onClick={() => set({ items: [...form.items, { description: 'Neue Position', quantity: 1, unit: 'Std', unit_price: 100 }] })} data-testid="designer-add-item"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_70px_90px_32px] gap-2" data-testid={`designer-item-${i}`}>
              <Input value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} />
              <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))} />
              <Input value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} />
              <Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', Number(e.target.value))} />
              <Button type="button" size="icon" variant="ghost" disabled={form.items.length <= 1} onClick={() => set({ items: form.items.filter((_, idx) => idx !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Schlusstext</Label>
            <Textarea rows={2} value={form.closing_text} onChange={(e) => set({ closing_text: e.target.value })} data-testid="designer-closing-text" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Logo (PNG/JPG, optional)</Label>
            <Input type="file" accept="image/png,image/jpeg" onChange={(e) => onLogo(e.target.files?.[0])} data-testid="designer-logo" />
            {form.logo_base64 && <button type="button" className="text-xs text-slate-500 underline" onClick={() => set({ logo_base64: null })}>Logo entfernen</button>}
          </div>
        </div>
      </div>

      <div className="space-y-3 xl:sticky xl:top-20 xl:self-start">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Vorschau · Seite 1{pages ? ` von ${pages}` : ''}</Label>
          <div className="flex items-center gap-2">
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            <Button type="button" size="sm" variant="outline" onClick={() => openPdf(false)} data-testid="designer-open-pdf"><ExternalLink className="mr-1 h-3.5 w-3.5" /> PDF</Button>
            <Button type="button" size="sm" onClick={() => openPdf(true)} data-testid="designer-open-all"><Layers className="mr-1 h-3.5 w-3.5" /> Alle Designs</Button>
          </div>
        </div>
        <div className="aspect-[210/297] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {preview ? <img src={preview} alt="Rechnungs-Vorschau" className="h-full w-full object-contain" data-testid="designer-preview-img" />
            : <div className="flex h-full items-center justify-center text-sm text-slate-400">Vorschau wird gerendert …</div>}
        </div>
      </div>
    </div>
  );
}
